import React from "react";
import { colors2 } from "oolib";
import { ArrowUpRight, ArrowDownRight, AsteriskSimple, Minus } from "@phosphor-icons/react";
import { StatusPill } from "./StatusPill";

// The recurring new / rising / declining / stable pill, shared across LensesV2
// widgets. The trend VALUE is computed upstream by `computeTrend` (LensesV2/utils);
// this just renders it. The icon + label ALWAYS reflect the real direction.
//
// Colour:
//   - variant "neutral" (default, Topics): everything grey — fully informational,
//     including NEW. Direction carries no good/bad meaning.
//   - variant "trend" (Signals): NEW is always purple, STABLE always grey, and
//     rising/declining are coloured by `tone`:
//       "good"    → rising green, declining red
//       "bad"     → rising red,   declining green
//       "neutral" → rising/declining grey (no good/bad), but NEW stays purple
//
// (v1 recoloured by swapping the rising/declining THEME, which also swapped icon +
// label — a rising signal could render a DOWN arrow + "DECLINING". Here colour and
// direction are decoupled, so the label is always honest.)
const TREND_META = {
  rising: { Icon: ArrowUpRight, label: "RISING" },
  declining: { Icon: ArrowDownRight, label: "DECLINING" },
  new: { Icon: AsteriskSimple, label: "NEW" },
  stable: { Icon: Minus, label: "STABLE" },
};

const GREY = { bg: "#03143033", color: colors2.grey70 };
const GREEN = { bg: "#6BDC58", color: colors2.grey80 };
const RED = { bg: "#FF7F7F", color: colors2.grey80 };
const PURPLE = { bg: "#B19DFF", color: colors2.grey80 };

function resolveColor(trend, variant, tone) {
  if (variant === "neutral") return GREY; // Topics — fully muted, incl. NEW.
  if (trend === "new") return PURPLE; // "trend" variant: NEW always highlighted.
  if (trend === "stable") return GREY;
  if (tone === "good") return trend === "rising" ? GREEN : RED;
  if (tone === "bad") return trend === "rising" ? RED : GREEN;
  return GREY; // tone "neutral" → rising/declining carry no good/bad colour.
}

export function TrendPill({ trend = "stable", variant = "neutral", tone = "neutral", iconSize = 16, as }) {
  const meta = TREND_META[trend] || TREND_META.stable;
  const color = resolveColor(trend, variant, tone);
  return <StatusPill theme={{ ...meta, ...color }} iconSize={iconSize} as={as} />;
}
